import { createReducer, createAction } from 'redux-act'
import { selector as configSelector } from '~/config'
import { select, apply, put, takeEvery, all } from 'redux-saga/effects'
import { get } from 'lodash'
import i18n from '~/i18n'
const __ = i18n.t.bind(i18n);

import { Entity, handleError } from '~/utils/entity-utils'

import API from '~/api'

const entityName = 'order';
const entity = new Entity(entityName, API, configSelector);

export const getShippingState = (state) => {
  const states = {
    "on_hold": __("On Hold"),
    "partially_queued": __("Partially Queued"),
    "queued": __("Queued"),
    "dequeued": __("Dequeued"),
    "picker_assigned": __("Picker assigned"),
    "picked": __("Picked"),
    "placed": __("Placed"),
    "packer_assigned": __("Packer assigned"),
    "packed": __("Packed"),
    "inbound": __("Inbound"),
    "received": __("Received"),
    "sorted": __("Sorted"),
    "shipped": __("Shipped"),
    "in_transport": __("In Transport"),
    "in_terminal": __("In Terminal"),
    "out_for_delivery": __("Out for Delivery"),
    "delivered": __("Delivered"),
    "returned": __("Returned"),
    "canceled": __("Cancelled")
  }

  return states[state] || state;
}

export const getShippingStateColor = (state) => {
  const successStates = ['queued', 'picker_assigned', 'picked', 'placed', 'packer_assigned', 'packed', 'sorted',
    'shipped', 'in_transport', 'in_terminal', 'delivered'];
  const errorStates = ['returned', 'canceled',]

  if(successStates.indexOf(state) > -1) {
    return 'green'
  } else if(errorStates.indexOf(state) > -1) {
    return 'red'
  }
  return 'blue'
}

// ACTIONS
export const actions = {
  ...entity.actions,
  orderAction: {
    request: createAction('order action request', (actionUrl) => ({actionUrl})),
    failure: createAction('order action failure', (message) => ({message})),
    success: createAction('order action success', (order) => ({order}))
  },
  orderExport: {
    request: createAction('order export request', (filters, format) => ({filters, format})),
    failure: createAction('order export failure', (message) => ({message})),
    success: createAction('order export success', (order) => ({order}))
  }
};

// WORKERS
export const workers = {
  ...entity.workers,
  * applyOrderAction(action) {
    const { actionUrl, params } = get(action, 'payload', {});

    try {
      const config = yield select(configSelector);
      const api = new API(config);
      const response = yield apply(api, api.orderAction, [actionUrl, params]);

      yield put(actions.orderAction.success(response.data));
    } catch (e) {
      handleError(e, actions.orderAction);
      throw e;
    }
  },
  * exportOrders(action) {
    const { filters, format, shopId } = get(action, 'payload', {});

    try {
      const config = yield select(configSelector);
      const api = new API(config);
      const response = yield apply(api, api.orderExport, [filters, format, shopId]);

      yield put(actions.orderExport.success(response));

      return response;
    } catch (e) {
      handleError(e, actions.orderExport);
      throw e;
    }
  }
};

// SAGA
export function * saga() {
  yield all([
    ...entity.saga,
    takeEvery(actions.orderAction.request().type, workers.applyOrderAction)
  ]);
}

export const STATUS_PENDING = 'pending';
export const STATUS_RECEIVED = 'received';
export const STATUS_SHIPPED = 'shipped';
export const STATUS_COMPLETED = 'completed';
export const STATUS_CANCELLED = 'cancelled';

export const getStatusOptions = () => ([
  { value: STATUS_PENDING, label: __('Pending'), text: __('Pending') },
  { value: STATUS_RECEIVED, label: __('Received'), text: __('Received')  },
  { value: STATUS_SHIPPED, label: __('Shipped'), text: __('Shipped') },
  { value: STATUS_COMPLETED, label: __('Completed'), text: __('Completed') },
  { value: STATUS_CANCELLED, label: __('Cancelled'), text: __('Cancelled') },
]);

export const PAYMENT_STATUS_PENDING = 'pending';
export const PAYMENT_STATUS_PAID = 'paid';
export const PAYMENT_STATUS_FAILED = 'failed';
export const PAYMENT_STATUS_AUTHORIZED = 'authorized';

export const getPaymentStatusOptions = () => ([
  { value: PAYMENT_STATUS_PENDING, label: __('Pending'), text: __('Pending') },
  { value: PAYMENT_STATUS_PAID, label: __('Paid'), text: __('Paid') },
  { value: PAYMENT_STATUS_FAILED, label: __('Payment Failed'), text: __('Payment Failed') },
  { value: PAYMENT_STATUS_AUTHORIZED, label: __('Authorized'), text: __('Authorized') },
]);

export const ACTION_RECEIVE = 'receive';
export const ACTION_PAY = 'pay';
export const ACTION_SHIP = 'ship';
export const ACTION_COMPLETE = 'complete';
export const ACTION_CANCEL = 'cancel';
export const ACTION_FAIL_PAYMENT = 'failpayment';

export const ACTION_RESEND_INVOICE = 'resendinvoice';

export const getOrderStatusActions = () => ([
  { value: ACTION_RECEIVE, label: __('Set Order Received'), text: __('Set Order Received') },
  { value: ACTION_PAY, label: __("Set Order Paid"), text: __("Set Order Paid") },
  { value: ACTION_SHIP, label: __("Set Order Shipped"), text: __("Set Order Shipped") },
  { value: ACTION_COMPLETE, label: __("Complete Order"), text: __("Complete Order") },
  { value: ACTION_CANCEL, label: __("Cancel Order"), text: __("Cancel Order") },
  { value: ACTION_FAIL_PAYMENT, label: __("Set Order Payment Failed"), text: __("Set Order Payment Failed") },
]);

export const getOrderActions = () => {
  const statusActions = getOrderStatusActions();

  return [
    ...statusActions,
    { value: ACTION_RESEND_INVOICE, label: __("Re-send invoice"), text: __("Re-send invoice") },
  ];
};

export const reducer = createReducer({
  ...entity.reducer,
  [actions.orderAction.success]: (state, payload) =>  Object.assign({}, state, {[payload[entityName]['url']]: payload[entityName]}),
}, {});

export default {
  actions,
  workers,
  saga,
  reducer
}
